import { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { SpaceFileIntent } from 'shared';

import { BackendRoute } from '../../../config/index';
import { useLocale } from '../../../hooks/use-locale.hook';
import { ApiService } from '../../../services/index';
import { SPACE_BACKEND_ROUTE } from '../constants/spaces-query.const';
import { TreeSpaceRef } from '../types/data-room.types';
import { DataRoomViewer, useDataRoomViewer } from './use-recipient-view.hook';

/**
 * Preview of one file of a workspace or a data room: the server answers the `view` intent with the
 * bytes, a type the browser can show opens in a tab of its own, and any other is saved instead and
 * still counts as a view. The same file asked for again while its request is out is not asked for
 * twice.
 *
 * Which route carries it is decided before the request, as for a download: the owner's module
 * routes refuse a recipient, and a recipient's route is scoped to the room they were shared.
 */
export const useViewSpaceFile = () => {
  const viewer = useDataRoomViewer();
  const { messagesLocale } = useLocale();
  const viewing = useRef(new Set<string>());

  return useCallback(
    async ({ fileId, space }: { fileId: string; space: TreeSpaceRef }) => {
      if (viewer === DataRoomViewer.UNRESOLVED || viewing.current.has(fileId)) return;

      viewing.current.add(fileId);

      try {
        await ApiService.previewFile(
          {
            endpoint:
              viewer === DataRoomViewer.RECIPIENT
                ? BackendRoute.MY_DATA_ROOMS
                : SPACE_BACKEND_ROUTE[space.kind],
            routePath: [space.id, 'files', fileId, 'download'],
          },
          { queryParams: { intent: SpaceFileIntent.VIEW } },
        );
      } catch {
        toast.error(messagesLocale['FILE_VIEW_FAILED']);
      } finally {
        viewing.current.delete(fileId);
      }
    },
    [messagesLocale, viewer],
  );
};
